import styled, { css } from "styled-components";
import { shade950, shade1000 } from "../../styles/tokens";

// TEXT

// STYLES
const overlayStyles = css`
  position: fixed;
  inset: 0;
  min-height: 100vh;
  max-height: 100vh;
  z-index: 9999;
`;

const noOverlayStyles = css`
  width: 100%;
  height: 100%;
  background-color: ${shade1000};
`;

const backgroundStyles = ({ $opaque, $overlay }: ContainerProps) => {
  if ($opaque) {
    return css`
      background-color: ${shade950};
    `;
  }

  if ($overlay) {
    return css`
      background-color: ${shade1000};
    `;
  }
};

export const Container = styled.div<ContainerProps>`
  ${({ $inline }) => ($inline ? "" : "flex: 1;")}
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $inline, $overlay = false }) =>
    $inline ? "" : $overlay ? overlayStyles : noOverlayStyles}
  ${({ $inline, ...rest }) => ($inline ? "" : backgroundStyles(rest))}
`;

export const Splash = styled.div<{ $inline?: boolean }>`
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  ${({ $inline }) =>
    $inline
      ? ""
      : `
    row-gap: 1rem;
    max-height: 25vh;
  `}
`;

export const LogoWrapper = styled.div<{ $inline?: boolean }>`
  max-width: 6vw;
  ${({ $inline }) =>
    $inline
      ? ""
      : `
    min-width: 8rem;
    min-height: 8rem;
  `}
`;

// DATA

// TYPES
export interface Props {
  loop?: boolean;
  overlay?: boolean;
  opaque?: boolean;
  inline?: boolean;
  delayAnimation?: number;
  onComplete?: () => void;
}

export type ContainerProps = {
  $overlay?: boolean;
  $opaque?: boolean;
  $inline?: boolean;
};

// CONSTANTS
export const ANIMATION_DURATION = 1500; // in milliseconds
export const TOTAL_FRAMES = 48;
export const CALCULATED_FRAME_RATE = (TOTAL_FRAMES / ANIMATION_DURATION) * 1000;

// FUNCTIONS

// ASSETS
export const AnimatedLogo = {
  nm: "MM-logo-animate-in-v2",
  ddd: 0,
  h: 250,
  w: 500,
  meta: { g: "@lottiefiles/toolkit-js 0.67.2" },
  layers: [
    {
      ty: 0,
      nm: "MM - Logo - Animation",
      sr: 1,
      st: 7,
      op: 154,
      ip: 7,
      ln: "1752",
      hasMask: false,
      ao: 0,
      ks: {
        a: { a: 0, k: [375, 180] },
        s: { a: 0, k: [71.429, 71.429, 100] },
        p: { a: 0, k: [250, 125.286, 0] },
        r: { a: 0, k: 0 },
        sa: { a: 0, k: 0 },
        o: { a: 0, k: 100 },
      },
      ef: [
        {
          ty: 21,
          nm: "Fill",
          en: 1,
          ef: [
            { ty: 10, nm: "Fill Mask", v: { a: 0, k: 0 } },
            { ty: 7, nm: "All Masks", v: { a: 0, k: 0 } },
            { ty: 2, nm: "Color", v: { a: 0, k: [1, 0.3059, 0] } },
            { ty: 7, nm: "Invert", v: { a: 0, k: 0 } },
            { ty: 0, nm: "Horizontal Feather", v: { a: 0, k: 0 } },
            { ty: 0, nm: "Vertical Feather", v: { a: 0, k: 0 } },
            { ty: 0, nm: "Opacity", v: { a: 0, k: 1 } },
          ],
        },
      ],
      w: 750,
      h: 360,
      refId: "1",
      ind: 1,
    },
    {
      ty: 0,
      nm: "MM - Logo - Animation",
      sr: 1,
      st: -54,
      op: 161,
      ip: -54,
      ln: "1751",
      hasMask: false,
      ao: 0,
      ks: {
        a: { a: 0, k: [375, 180] },
        s: { a: 0, k: [71.429, 71.429, 100] },
        p: { a: 0, k: [250, 125.286, 0] },
        r: { a: 0, k: 0 },
        sa: { a: 0, k: 0 },
        o: { a: 0, k: 100 },
      },
      ef: [
        {
          ty: 21,
          nm: "Fill",
          en: 1,
          ef: [
            { ty: 10, nm: "Fill Mask", v: { a: 0, k: 0 } },
            { ty: 7, nm: "All Masks", v: { a: 0, k: 0 } },
            { ty: 2, nm: "Color", v: { a: 0, k: [1, 1, 1] } },
            { ty: 7, nm: "Invert", v: { a: 0, k: 0 } },
            { ty: 0, nm: "Horizontal Feather", v: { a: 0, k: 0 } },
            { ty: 0, nm: "Vertical Feather", v: { a: 0, k: 0 } },
            { ty: 0, nm: "Opacity", v: { a: 0, k: 1 } },
          ],
        },
      ],
      w: 750,
      h: 360,
      refId: "1",
      ind: 2,
    },
  ],
  v: "5.7.0",
  fr: CALCULATED_FRAME_RATE,
  op: TOTAL_FRAMES,
  ip: 0,
  assets: [
    {
      nm: "MM - Logo - Animation",
      id: "1",
      fr: CALCULATED_FRAME_RATE,
      layers: [
        {
          ty: 4,
          nm: "Mask",
          sr: 1,
          st: 0,
          op: 361,
          ip: 0,
          ln: "362",
          hasMask: false,
          td: 1,
          ao: 0,
          ks: {
            a: { a: 0, k: [0, 0] },
            s: { a: 0, k: [100, 100] },
            p: { a: 0, k: [0.513, 54.858, 0] },
            r: { a: 0, k: 0 },
            sa: { a: 0, k: 0 },
            o: { a: 0, k: 100 },
          },
          shapes: [
            {
              ty: "gr",
              nm: "Rectangle 1",
              it: [
                {
                  ty: "rc",
                  nm: "Rectangle Path 1",
                  d: 1,
                  p: { a: 0, k: [0, 0] },
                  r: { a: 0, k: 0 },
                  s: { a: 0, k: [183, 123] },
                },
                {
                  ty: "st",
                  nm: "Stroke 1",
                  lc: 1,
                  lj: 1,
                  ml: 4,
                  o: { a: 0, k: 100 },
                  w: { a: 0, k: 2 },
                  c: { a: 0, k: [1, 1, 1] },
                },
                {
                  ty: "fl",
                  nm: "Fill 1",
                  c: { a: 0, k: [1, 0, 0] },
                  r: 1,
                  o: { a: 0, k: 100 },
                },
                {
                  ty: "tr",
                  a: { a: 0, k: [0, 0] },
                  s: { a: 0, k: [100, 100] },
                  p: { a: 0, k: [-362.5, 138.5] },
                  r: { a: 0, k: 0 },
                  sa: { a: 0, k: 0 },
                  o: { a: 0, k: 100 },
                },
              ],
            },
          ],
          ind: 1,
          parent: 2,
        },
        {
          ty: 4,
          nm: "MM symbol - path 3",
          sr: 1,
          st: 0,
          op: 361,
          ip: 0,
          ln: "361",
          tt: 2,
          hasMask: true,
          ao: 0,
          ks: {
            a: { a: 0, k: [0, 0] },
            s: { a: 0, k: [70, 70, 100] },
            p: { a: 0, k: [375.141, 183.099, 0] },
            r: { a: 0, k: 0 },
            sa: { a: 0, k: 0 },
            o: { a: 0, k: 100 },
          },
          ef: [
            {
              ty: 21,
              nm: "Fill",
              en: 1,
              ef: [
                { ty: 10, nm: "Fill Mask", v: { a: 0, k: 0 } },
                { ty: 7, nm: "All Masks", v: { a: 0, k: 0 } },
                { ty: 2, nm: "Color", v: { a: 0, k: [1, 1, 1] } },
                { ty: 7, nm: "Invert", v: { a: 0, k: 0 } },
                { ty: 0, nm: "Horizontal Feather", v: { a: 0, k: 0 } },
                { ty: 0, nm: "Vertical Feather", v: { a: 0, k: 0 } },
                { ty: 0, nm: "Opacity", v: { a: 0, k: 1 } },
              ],
            },
          ],
          masksProperties: [
            {
              nm: "Mask 1",
              inv: false,
              mode: "a",
              x: { a: 0, k: 0 },
              o: { a: 0, k: 100 },
              pt: {
                a: 0,
                k: {
                  c: true,
                  i: [
                    [0, 0],
                    [0, 0],
                    [0, 0],
                    [0, 0],
                  ],
                  o: [
                    [0, 0],
                    [0, 0],
                    [0, 0],
                    [0, 0],
                  ],
                  v: [
                    [537.656, -262.285],
                    [-539.487, -262.285],
                    [-539.487, 256.287],
                    [537.656, 256.287],
                  ],
                },
              },
            },
          ],
          shapes: [
            {
              ty: "gr",
              nm: "Rectangle 1",
              it: [
                {
                  ty: "rc",
                  nm: "Rectangle Path 1",
                  d: 1,
                  p: { a: 0, k: [0, 0] },
                  r: { a: 0, k: 0 },
                  s: { a: 0, k: [0, 2] },
                },
                {
                  ty: "st",
                  nm: "Stroke 1",
                  lc: 1,
                  lj: 1,
                  ml: 4,
                  o: { a: 0, k: 100 },
                  w: { a: 0, k: 823 },
                  c: { a: 0, k: [1, 0, 1] },
                },
                {
                  ty: "fl",
                  nm: "Fill 1",
                  c: { a: 0, k: [1, 0, 0] },
                  r: 1,
                  o: { a: 0, k: 100 },
                },
                {
                  ty: "tr",
                  a: { a: 0, k: [0, 0] },
                  s: { a: 0, k: [100, 100] },
                  p: { a: 0, k: [-1547.487, -252.142] },
                  r: { a: 0, k: 0 },
                  sa: { a: 0, k: 0 },
                  o: { a: 0, k: 100 },
                },
              ],
            },
            {
              ty: "gr",
              nm: "Group 1",
              it: [
                {
                  ty: "sh",
                  nm: "Path 1",
                  d: 1,
                  ks: {
                    a: 0,
                    k: {
                      c: false,
                      i: [
                        [0, 0],
                        [0, 0],
                        [-28.095, 0],
                        [0, -28.094],
                        [0, 0],
                        [-28.094, 0],
                        [0, 28.093],
                        [0, 0],
                        [-28.094, 0],
                        [0, -28.094],
                        [0, 0],
                        [-28.094, 0],
                        [0, 28.093],
                        [0, 0],
                        [-28.094, 0],
                        [0, -28.094],
                        [0, 0],
                        [-28.094, 0],
                        [0, 28.093],
                        [0, 0],
                        [-28.094, 0],
                        [0, -28.094],
                        [0, 0],
                      ],
                      o: [
                        [0, 0],
                        [0, -28.094],
                        [28.093, 0],
                        [0, 0],
                        [0, 28.093],
                        [28.094, 0],
                        [0, 0],
                        [0, -28.094],
                        [28.094, 0],
                        [0, 0],
                        [0, 28.093],
                        [28.094, 0],
                        [0, 0],
                        [0, -28.094],
                        [28.094, 0],
                        [0, 0],
                        [0, 28.093],
                        [28.094, 0],
                        [0, 0],
                        [0, -28.094],
                        [28.094, 0],
                        [0, 0],
                        [0, 0],
                      ],
                      v: [
                        [-356.65, 131.229],
                        [-356.65, -101.691],
                        [-305.699, -152.641],
                        [-254.75, -101.691],
                        [-254.75, 49.026],
                        [-203.8, 99.975],
                        [-152.85, 49.026],
                        [-152.85, -101.691],
                        [-101.9, -152.641],
                        [-50.95, -101.691],
                        [-50.95, 101.692],
                        [0, 152.641],
                        [50.95, 101.692],
                        [50.95, -101.691],
                        [101.9, -152.641],
                        [152.85, -101.691],
                        [152.85, 49.026],
                        [203.8, 99.975],
                        [254.75, 49.026],
                        [254.75, -101.691],
                        [305.7, -152.641],
                        [356.65, -101.691],
                        [356.65, 109.107],
                      ],
                    },
                  },
                },
                {
                  ty: "st",
                  nm: "Stroke 1",
                  lc: 2,
                  lj: 1,
                  ml: 10,
                  o: { a: 0, k: 100 },
                  w: { a: 0, k: 78 },
                  c: { a: 0, k: [1, 0, 1] },
                },
                {
                  ty: "tr",
                  a: { a: 0, k: [0, 0] },
                  s: { a: 0, k: [100, 100] },
                  p: { a: 0, k: [0, 0] },
                  r: { a: 0, k: 0 },
                  sa: { a: 0, k: 0 },
                  o: { a: 0, k: 100 },
                },
              ],
            },
            {
              ty: "tm",
              nm: "Trim Paths 1",
              e: {
                a: 1,
                k: [
                  {
                    o: { x: 0.253, y: 0 },
                    i: { x: 0.567, y: 1 },
                    s: [0],
                    t: 0,
                  },
                  { s: [100], t: 40 },
                ],
              },
              o: { a: 0, k: 0 },
              s: { a: 0, k: 0 },
              m: 1,
            },
          ],
          ind: 2,
          tp: 1,
        },
      ],
    },
  ],
};
